import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Checkbox,
  Text,
  Select,
  FormRow,
  Input
} from '@efast_public/fjd-component-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DeliveryServiceResponse, Zustellwege } from '../api/govos.types';
import { GovosApiRoutes } from '../api/govos-api';
import { ZustellwegModalRouter } from './zustellweg/ZustellwegModalRouter';
import {
  AvailableZustellwege,
  ZustellwegTypes
} from './zustellweg/Zustellweg.types';
import { useTemplateNameValidation } from '../hooks/useTemplateNameValidation';

interface EditTransportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  startAppId: string | number;
  processId?: string | number;
  templateId?: string | number;
  zustellwege?: Zustellwege[];
  serviceIndex: number;
  isTemplatePage?: boolean;
  templateName?: string;
  existingNames?: string[];
}

export function EditZustellwegConfigModal({
  isOpen,
  onClose,
  startAppId,
  processId,
  templateId,
  zustellwege,
  serviceIndex,
  onSuccess,
  isTemplatePage,
  templateName,
  existingNames
}: EditTransportConfigModalProps) {
  const { t } = useTranslation();

  const [primaryButtonClickCount, setPrimaryButtonClickCount] = useState(0);
  const [selectedZustellwegType, setSelectedZustellwegType] =
    useState<ZustellwegTypes | null>(null);
  const [shouldSaveAsTemplate, setShouldSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateNameInModal, setTemplateNameInModal] = useState(
    templateName ?? ''
  );

  const isTemplateMode = templateId != null;

  const { templateNameError, validateTemplateName, clearTemplateNameError } =
    useTemplateNameValidation({
      currentTemplateName: isTemplatePage ? templateName : undefined,
      existingNames,
      t,
      validationKeyPrefix: 'EditTransportConfigModal'
    });

  const { data: deliveryServices } = useQuery<DeliveryServiceResponse>({
    queryKey: [GovosApiRoutes.deliveryServices(startAppId)]
  });

  useEffect(() => {
    if (deliveryServices && serviceIndex > -1) {
      const deliveryService = zustellwege?.at(serviceIndex);
      if (deliveryService) {
        setSelectedZustellwegType(deliveryService.serviceCode);
      }
    }
  }, [deliveryServices]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditTransportConfigModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <FormRow label={t('EditTransportConfigModal.fields.zustellweg')}>
            <Select
              onChange={(value) => {
                setSelectedZustellwegType(
                  value?.at(0)?.value as ZustellwegTypes
                );
              }}
              options={AvailableZustellwege}
              placeholder={t(
                'EditTransportConfigModal.fields.selectZustellweg'
              )}
              defaultOptions={
                deliveryServices && serviceIndex > -1
                  ? AvailableZustellwege.filter(
                      (zs) =>
                        zs.value === zustellwege?.at(serviceIndex)?.serviceCode
                    )
                  : []
              }
            />
          </FormRow>
          {isTemplatePage && isTemplateMode && (
            <FormRow
              label={t('EditTransportConfigModal.templateName.label')}
              validationMessage={templateNameError}
              variant={templateNameError ? 'error' : undefined}
            >
              <Input
                value={templateNameInModal}
                onChange={(e) => {
                  setTemplateNameInModal(e.target.value);
                  clearTemplateNameError();
                }}
                placeholder={t(
                  'EditTransportConfigModal.templateName.placeholder'
                )}
                style={{ width: '100%' }}
              />
            </FormRow>
          )}

          {selectedZustellwegType && deliveryServices && (
            <ZustellwegModalRouter
              serviceIndex={serviceIndex}
              parentState={{ selectedZustellwegType }}
              deliveryServices={deliveryServices.deliveryServices}
              primaryButtonClickCount={primaryButtonClickCount}
              setPrimaryButtonClickCount={setPrimaryButtonClickCount}
              startAppId={startAppId}
              processId={processId}
              templateId={templateId}
              zustellwege={zustellwege}
              onSuccess={onSuccess}
              isParentFormValid
              shouldSaveAsTemplate={shouldSaveAsTemplate}
              newTemplateName={newTemplateName}
            />
          )}

          {!isTemplatePage && !isTemplateMode && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Checkbox
                checked={shouldSaveAsTemplate}
                onChange={(e) =>
                  setShouldSaveAsTemplate(e.currentTarget.checked)
                }
                label={t('EditTransportConfigModal.saveAsTemplate.checkbox')}
              />
              {shouldSaveAsTemplate && (
                <FormRow
                  label={t('EditTransportConfigModal.saveAsTemplate.nameLabel')}
                  validationMessage={templateNameError}
                  variant={templateNameError ? 'error' : undefined}
                >
                  <Input
                    value={newTemplateName}
                    onChange={(e) => {
                      setNewTemplateName(e.target.value);
                      clearTemplateNameError();
                    }}
                    placeholder={t(
                      'EditTransportConfigModal.saveAsTemplate.namePlaceholder'
                    )}
                    style={{ width: '100%' }}
                  />
                </FormRow>
              )}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button variant="secondary-outline" onClick={onClose}>
            {t('EditTransportConfigModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={(zustellwege?.length ?? 0) > 0 && serviceIndex === -1}
            onClick={() => {
              if (shouldSaveAsTemplate && !isTemplateMode) {
                if (!validateTemplateName(newTemplateName)) {
                  return;
                }
              }
              if (isTemplatePage && isTemplateMode) {
                if (!validateTemplateName(templateNameInModal)) {
                  return;
                }
              }
              setPrimaryButtonClickCount(primaryButtonClickCount + 1);
            }}
          >
            {t('EditTransportConfigModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
